import { mergeGraph } from "@app/core";
import { HttpSyncClient } from "@app/storage";
import { create } from "zustand";
import { getServices } from "../platform.js";
import {
  ackServerPush,
  applySyncGraph,
  chunkGraph,
  dirtyPreferences,
  gatherLocalSyncState,
  graphCount,
  mutBaseline,
  serverDirtyGraph,
} from "../lib/sync-graph.js";
import {
  loadSyncMetadata,
  loadSyncUrl,
  saveSyncMetadata,
  SYNC_URL_KEY,
} from "../lib/sync-metadata.js";
import { DEFAULT_SYNC_URL } from "../lib/sync-server.js";
import { reconcileDuplicateEntities } from "../lib/dedupe-entities.js";
import { acquireSyncLane } from "../lib/sync-lane.js";
import { accountGeneration } from "../lib/account-session.js";
import { reconcileThemeSync } from "../features/theme/theme-sync.js";
import { rehydrateStores } from "../lib/snapshot.js";
import { tr } from "../lib/tr.js";
import { useAuthStore } from "./auth.store.js";
import { toast } from "./toast.store.js";

/** Rows per push request. A normal incremental sync is one tiny batch; a large
 *  first-time sync splits so the status line shows real X-of-Y upload progress.
 *  The server upserts each batch in bulk (a few queries), so 1000 stays fast. */
const PUSH_CHUNK_SIZE = 1000;

/** Unwinds a pass whose account was cleared (or replaced) while it was in
 *  flight. Never surfaced to the user — see the catch in `syncNow`. */
class AccountChanged extends Error {
  constructor() {
    super("account changed during sync");
  }
}

interface SyncState {
  url: string;
  cursor: string | null;
  /** DISPLAY ONLY — shown in the settings card. Dirtiness never derives from it
   *  (per-row `mut > server_acked_mut` is the queue; see lib/sync-graph.ts). */
  lastSyncAt: string | null;
  /** Set (IN MEMORY only, never persisted) when a pass completes this session.
   *  The Google reconciler reads it to order its FIRST pass after a server sync:
   *  a device offline for weeks can hold a stale link generation, and pushing to
   *  Google before pulling the current one re-opens the duplicate-event race
   *  (its derived event id points at a tombstone → random-id fallback).
   *  ACCOUNT-SCOPED — read it through {@link serverSyncedThisSession}, never
   *  raw: it belongs to `sessionSyncedGen`'s account, and a sign-out/sign-in
   *  makes it a claim about somebody else's server. */
  sessionSyncedAt: string | null;
  /** The account generation `sessionSyncedAt` was stamped under. */
  sessionSyncedGen: number | null;
  busy: boolean;
  /** Human-readable current step while `busy` (e.g. "Pulling changes…"), so the
   *  UI shows progress instead of a static "Syncing…". Null when idle. */
  phase: string | null;
  error: string | null;
  loaded: boolean;
  load: () => Promise<void>;
  setUrl: (url: string) => Promise<void>;
  /** `silent` (used by the background reconciler) suppresses success/error toasts
   *  — conflicts and the status line still surface. */
  syncNow: (opts?: { silent?: boolean }) => Promise<void>;
}

/** Multi-device sync orchestrator. Reconcile = pull remote delta → merge
 *  (field-level LWW + set union, in `@app/core`) → apply locally → push the local
 *  dirty set → adopt the server's authoritative rows → advance the cursor.
 *  Authentication is owned by `auth.store`; the server URL lives in settings and
 *  is NEVER backed up (neither are the auth tokens). */
export const useSyncStore = create<SyncState>((set, get) => ({
  url: DEFAULT_SYNC_URL,
  cursor: null,
  lastSyncAt: null,
  sessionSyncedAt: null,
  sessionSyncedGen: null,
  busy: false,
  phase: null,
  error: null,
  loaded: false,

  async load() {
    const metadata = await loadSyncMetadata();
    set({
      url: await loadSyncUrl(),
      cursor: metadata.cursor,
      lastSyncAt: metadata.lastSyncAt,
      loaded: true,
    });
  },

  async setUrl(url) {
    await getServices().settings.set(SYNC_URL_KEY, url.trim());
    set({ url: url.trim() });
  },

  async syncNow(opts) {
    const silent = opts?.silent ?? false;
    const { url, busy } = get();
    if (busy) return; // never overlap a run (auto tick + manual click, etc.)
    // Claim the run SYNCHRONOUSLY — an await before setting busy would let a
    // concurrent caller (boot + window-focus fire together) slip past the guard.
    set({ busy: true, phase: "Connecting…", error: null });
    // Serialized against Google Calendar sync: both are debounced off the SAME
    // task store, so a local edit wakes both at once and they would otherwise
    // interleave over the same rows — the Google reconciler adopting an event's
    // identity mid-pass while this one is in flight with a pre-adoption snapshot
    // is exactly how a task ended up owning two Google events. `lib/sync-lane.ts`.
    const releaseLane = await acquireSyncLane();
    // Everything below belongs to THIS account. Ordinary account transitions
    // take this same lane and cannot bisect the pass. A dead refresh token is the
    // re-entrant exception: its callback already owns the lane, clears the state
    // directly and bumps the generation, so the checkpoints below unwind before
    // another durable write can belong to an account the device no longer has.
    const generation = accountGeneration();
    const assertSameAccount = () => {
      if (accountGeneration() !== generation) throw new AccountChanged();
    };
    const startedAt = new Date().toISOString();
    try {
      const { cursor, lastSyncAt } = await loadSyncMetadata();
      set({ cursor, lastSyncAt });
      const auth = useAuthStore.getState();
      if (!url) {
        if (!silent) toast.error(tr("toast.sync.setUrlFirst"));
        return;
      }
      if (!auth.accessToken) {
        if (!silent) toast.error(tr("toast.sync.signInFirst"));
        return;
      }
      const client = new HttpSyncClient(
        url,
        () => useAuthStore.getState().accessToken,
        () => useAuthStore.getState().refreshAccess({ syncLaneHeld: true }),
      );
      const local = await gatherLocalSyncState();

      // 1. Pull the server's delta and converge it into local storage. The theme
      //    is carried by the transport but NOT applied by the generic merge — it
      //    has a dedicated conflict-aware resolver (run below). The apply's
      //    baseline is the gather's own `mut` snapshot: a row written after the
      //    gather is shielded (kept local, stays dirty) instead of overwritten.
      const pulled = await client.pull(cursor);
      assertSameAccount();
      const remoteTheme = pulled.changes.themes[0] ?? null;
      const merged = mergeGraph(local.graph, pulled.changes);
      const pulledN = graphCount(merged.toApplyLocally);
      set({ phase: pulledN > 0 ? `Applying ${pulledN} change(s)…` : "Checking for changes…" });
      await applySyncGraph(
        merged.toApplyLocally,
        mutBaseline(local.raw),
        ({ done, total }) => {
          set({ phase: `Applying ${done}/${total} change(s)…` });
        },
        assertSameAccount,
      );
      assertSameAccount();

      // 2. Push everything the server has not acknowledged. The dirty set is
      //    read straight off each row (`mut > server_acked_mut`) from a
      //    POST-APPLY re-gather: rows the apply just wrote are self-acked (not
      //    re-pushed), rows it shielded are still dirty (pushed with the adopted
      //    rev, so they win the rev-first comparison), and a link adoption —
      //    which never touches `updated_at` — queues like any other write.
      const raw = (await gatherLocalSyncState()).raw;
      const outgoing = serverDirtyGraph(raw, await dirtyPreferences());
      const pushedOutN = graphCount(outgoing);
      let appliedN = 0;
      if (pushedOutN === 0) {
        set({ phase: "Up to date…" });
      } else {
        let done = 0;
        for (const batch of chunkGraph(outgoing, PUSH_CHUNK_SIZE)) {
          set({ phase: `Uploading ${done}/${pushedOutN}…` });
          const res = await client.push(batch);
          assertSameAccount();
          // Acknowledge at the `mut` each row's payload was READ at — an edit
          // that landed mid-flight keeps its row dirty for the next run — then
          // adopt the server's authoritative copies (they carry the assigned
          // `rev`; identical echoes skip the write, mid-push edits are shielded).
          await ackServerPush(raw, batch, assertSameAccount);
          await applySyncGraph(
            res.applied,
            mutBaseline(raw),
            ({ done: appliedDone, total: appliedTotal }) => {
              set({ phase: `Applying server copy ${appliedDone}/${appliedTotal}…` });
            },
            assertSameAccount,
          );
          assertSameAccount();
          appliedN += graphCount(res.applied);
          done += graphCount(batch);
        }
        set({ phase: `Uploading ${pushedOutN}/${pushedOutN}…` });
      }

      // 3. Advance the cursor to the PULL high-water mark only. Our own pushed
      //    rows are already adopted via `applied`; re-pulling them next time is a
      //    harmless no-op merge — and this avoids skipping a concurrent device's
      //    write whose seq landed between our pull and our push.
      assertSameAccount();
      await saveSyncMetadata(pulled.cursor, startedAt);
      set({
        cursor: pulled.cursor,
        lastSyncAt: startedAt,
        sessionSyncedAt: startedAt,
        sessionSyncedGen: generation,
      });

      // Theme reconcile (conflict-aware; independent of the data cursor). A theme
      // hiccup must not fail the data sync.
      try {
        assertSameAccount();
        await reconcileThemeSync(remoteTheme, client);
        assertSameAccount();
      } catch (e) {
        if (e instanceof AccountChanged) throw e;
        console.warn("theme sync skipped:", e);
      }

      // Collapse any same-name label/topic/calendar duplicates that converged
      // from another device (each device seeds its own defaults with fresh ids,
      // so a synced "Work" topic arrives with a different id than the local one).
      // Survivor is chosen deterministically so both devices keep the same row;
      // the rewrites/tombstones push on the next sync.
      assertSameAccount();
      const healed = await reconcileDuplicateEntities();
      assertSameAccount();

      // Only refresh the UI stores when something actually landed locally —
      // otherwise the auto reconciler would see its own no-op rehydrate as a
      // "change" and loop. (Applied push rows carry the server-assigned `rev`.)
      if (pulledN > 0 || appliedN > 0 || healed) {
        set({ phase: "Refreshing…" });
        assertSameAccount();
        await rehydrateStores();
        assertSameAccount();
      }

      if (merged.conflicts.length > 0) {
        const count = merged.conflicts.length;
        toast.info(tr("toast.sync.doneWithConflicts", {
          pulled: pulledN,
          pushed: pushedOutN,
          count,
          noun: tr(count === 1 ? "common.conflict" : "common.conflicts"),
        }));
      } else if (!silent) {
        toast.success(tr("toast.sync.done", { pulled: pulledN, pushed: pushedOutN }));
      }
    } catch (e) {
      // A dead refresh token cleared the account from inside this lane: not a
      // failure the user needs to see, and not an error state. Its direct wipe
      // already owns the final cursor/ack state.
      if (e instanceof AccountChanged) return;
      const msg = e instanceof Error ? e.message : String(e);
      set({ error: msg });
      if (!silent) toast.error(tr("toast.sync.failed", { error: msg }));
    } finally {
      releaseLane();
      set({ busy: false, phase: null });
    }
  },
}));

/** Whether a server pass has completed this session FOR THE CURRENT ACCOUNT.
 *  The Google reconciler orders its first pass behind one (see
 *  `googleSync.store.ts`); a sign-out/sign-in invalidates the claim, because the
 *  new account's link generations have not been pulled yet. */
export function serverSyncedThisSession(): boolean {
  const { sessionSyncedAt, sessionSyncedGen } = useSyncStore.getState();
  return sessionSyncedAt !== null && sessionSyncedGen === accountGeneration();
}
